import React, { useCallback, useEffect, useRef, useState } from "react";
import Lottie from "lottie-react";
import { FaVolumeUp, FaVolumeMute } from "react-icons/fa";
import {
  startSession,
  endSession,
} from "../../services/GameService";
import GameLayoutTopbar from "../layouts/GameLayoutTopbar";
import GameOverScreen from "../GameOverScreen";
import { usePlayClock } from "../common/usePlayClock";
import guideButterflyData from "./animations/MonarchButterflyHorizontal.json";
import playerButterflyData from "./animations/BlueButterflyHorizontal.json";
import sunflowerData from "./animations/SunflowerLottieAnimation.json";
import fireworksData from "../FindMiddle/animations/fireworks.json";
import forestAudio from "./audio/forest_birdsinging.mp3";
import {
  GUIDE_HOME,
  PLAYER_HOME,
  parseLevelParams,
  placeFlowers,
  generatePath,
  nextPathLength,
  scoreGame,
  headingDegrees,
  shortestRotation,
} from "./butterflyPath";
import "./ButterflyPathForm.css";

// How long the guide rests on each flower, and the pauses around a round.
const REST_MS = 500;
const LEAD_IN_MS = 700;
const SUCCESS_PAUSE_MS = 1400;
const MISTAKE_PAUSE_MS = 2200;

// Last frame of the sunflower animation = fully open. Flowers rest there and
// replay the bloom from the start whenever a butterfly lands on them.
const BLOOM_REST_FRAME = sunflowerData.op - 1;

const Flower = ({ flower, bloomKey, state, badge, onClick, disabled }) => {
  const lottieRef = useRef(null);

  useEffect(() => {
    const anim = lottieRef.current;
    if (!anim) return;
    if (bloomKey > 0) anim.goToAndPlay(0, true);
    else anim.goToAndStop(BLOOM_REST_FRAME, true);
  }, [bloomKey]);

  return (
    <button
      type="button"
      className={`bp-flower${state ? ` bp-flower--${state}` : ""}`}
      style={{ left: `${flower.x}%`, top: `${flower.y}%` }}
      onClick={onClick}
      disabled={disabled}
      aria-label={`Flower ${flower.id + 1}`}
    >
      <Lottie
        lottieRef={lottieRef}
        animationData={sunflowerData}
        loop={false}
        autoplay={false}
        onDOMLoaded={() =>
          lottieRef.current &&
          lottieRef.current.goToAndStop(BLOOM_REST_FRAME, true)
        }
      />
      {badge && <span className={`bp-badge bp-badge--${badge.type}`}>{badge.text}</span>}
    </button>
  );
};

const Butterfly = ({ className, pos, flightMs, animationData, flying }) => {
  const lottieRef = useRef(null);
  useEffect(() => {
    if (lottieRef.current) lottieRef.current.setSpeed(flying ? 2 : 0.6);
  }, [flying]);
  return (
    <div
      className={`bp-butterfly ${className}`}
      style={{
        left: `${pos.x}%`,
        top: `${pos.y}%`,
        transform: `translate(-50%, -50%) rotate(${pos.rot}deg)`,
        transitionDuration: `${flightMs}ms, ${flightMs}ms, 300ms`,
      }}
      aria-hidden="true"
    >
      <Lottie lottieRef={lottieRef} animationData={animationData} loop autoplay />
    </div>
  );
};

// Butterfly Path — a Corsi-style visual-spatial memory game. Each round the
// orange guide butterfly flies a path over the flowers; the player repeats it
// by tapping the flowers in the same order while the blue butterfly follows.
// A perfect round makes the next path one flower longer, a mistake one
// shorter. The player's level sets the flower count, starting path length,
// number of rounds and flight speed (core/data/game_levels.json).
// `rng` is injectable so tests can make layouts and paths deterministic.
const ButterflyPathForm = ({ gameRoutePath = "butterfly-path", rng = Math.random }) => {
  const [phase, setPhase] = useState("loading"); // loading|ready|watch|play|feedback|over
  const [levelNumber, setLevelNumber] = useState(1);
  const [params, setParams] = useState(parseLevelParams(null));
  const [flowers, setFlowers] = useState([]);
  const [path, setPath] = useState([]);
  const [pathLength, setPathLength] = useState(params.startLength);
  const [round, setRound] = useState(0); // 0-based index of the current round
  const [progress, setProgress] = useState(0); // correct taps so far this round
  const [mistake, setMistake] = useState(null); // {wrongId, expectedId}
  const [blooms, setBlooms] = useState({}); // flowerId -> replay counter
  const [litId, setLitId] = useState(null);
  const [guide, setGuide] = useState({ ...GUIDE_HOME, rot: 0, flying: false });
  const [player, setPlayer] = useState({ ...PLAYER_HOME, rot: 180, flying: false });
  const [audioOn, setAudioOn] = useState(false);

  const sessionIdRef = useRef(null);
  // The in-flight endSession (it also promotes/demotes the level); a restart
  // waits for it so the next game loads the new level.
  const savingRef = useRef(null);
  const playClock = usePlayClock();
  const timersRef = useRef([]);
  const roundsCorrectRef = useRef(0);
  const longestRef = useRef(0);
  const audioRef = useRef(null);
  const playerLandRef = useRef(null);

  const schedule = useCallback((fn, ms) => {
    timersRef.current.push(setTimeout(fn, ms));
  }, []);
  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  const bloom = (id) => setBlooms((b) => ({ ...b, [id]: (b[id] || 0) + 1 }));

  // Load the player's level and lay out the meadow. Anonymous players get
  // Level 1 with no session (startSession handles that).
  const setupGame = useCallback(async () => {
    clearTimers();
    setPhase("loading");
    let level = null;
    try {
      if (savingRef.current) await savingRef.current;
      savingRef.current = null;
      const session = await startSession(gameRoutePath);
      sessionIdRef.current = (session && session.sessionId) || null;
      level = session && session.level;
    } catch (e) {
      // play with defaults
    }
    const p = parseLevelParams(level);
    setLevelNumber((level && level.level_number) || 1);
    setParams(p);
    setFlowers(placeFlowers(p.flowers, rng));
    setPathLength(p.startLength);
    setRound(0);
    roundsCorrectRef.current = 0;
    longestRef.current = 0;
    setGuide({ ...GUIDE_HOME, rot: 0, flying: false });
    setPlayer({ ...PLAYER_HOME, rot: 180, flying: false });
    setPhase("ready");
  }, [gameRoutePath, rng]);

  useEffect(() => {
    setupGame();
    return () => {
      clearTimers();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [setupGame]);

  // Ambient forest sound, created lazily (it's a large file) on first use.
  useEffect(() => {
    if (audioOn) {
      if (!audioRef.current) {
        audioRef.current = new Audio(forestAudio);
        audioRef.current.loop = true;
        audioRef.current.volume = 0.4;
      }
      const played = audioRef.current.play();
      if (played && played.catch) played.catch(() => {});
    } else if (audioRef.current) {
      audioRef.current.pause();
    }
  }, [audioOn]);

  // Fly a butterfly (via its state setter) to a point, turning to face it.
  const flyTo = (setter, target) =>
    setter((b) => ({
      x: target.x,
      y: target.y,
      rot: shortestRotation(b.rot, headingDegrees(b, target)),
      flying: true,
    }));
  const land = (setter) => setter((b) => ({ ...b, flying: false }));

  // Fly the player butterfly back to its perch facing the way it's going, then
  // turn it round to its resting pose facing the board.
  const flyPlayerHome = () => {
    const rest = (b) => ({ ...PLAYER_HOME, rot: shortestRotation(b.rot, 180), flying: false });
    setPlayer((b) => {
      if (b.x === PLAYER_HOME.x && b.y === PLAYER_HOME.y) return rest(b);
      return {
        ...PLAYER_HOME,
        rot: shortestRotation(b.rot, headingDegrees(b, PLAYER_HOME)),
        flying: true,
      };
    });
    schedule(() => setPlayer(rest), params.flightMs);
  };

  // Play one round's demonstration: the guide visits each flower of `newPath`
  // in turn, then flies home and hands over to the player.
  const playDemo = (newPath) => {
    const step = params.flightMs + REST_MS;
    newPath.forEach((id, i) => {
      const flower = flowers.find((f) => f.id === id);
      const t = LEAD_IN_MS + i * step;
      schedule(() => flyTo(setGuide, flower), t);
      schedule(() => {
        land(setGuide);
        setLitId(id);
        bloom(id);
      }, t + params.flightMs);
      schedule(() => setLitId(null), t + step);
    });
    const end = LEAD_IN_MS + newPath.length * step;
    schedule(() => flyTo(setGuide, GUIDE_HOME), end);
    schedule(() => {
      land(setGuide);
      setPhase("play");
    }, end + params.flightMs);
  };

  const startRound = (length) => {
    clearTimers();
    const newPath = generatePath(flowers, length, rng);
    setPath(newPath);
    setProgress(0);
    setMistake(null);
    setLitId(null);
    setGuide((b) => ({ ...GUIDE_HOME, rot: shortestRotation(b.rot, 0), flying: false }));
    flyPlayerHome();
    setPhase("watch");
    playDemo(newPath);
  };

  const startGame = () => {
    playClock.start();
    startRound(pathLength);
  };

  const finishRound = (success) => {
    if (success) {
      roundsCorrectRef.current += 1;
      longestRef.current = Math.max(longestRef.current, path.length);
    }
    const played = round + 1;
    // Last answer in: play time stops here, not after the closing pause.
    if (played >= params.rounds) playClock.stop();
    schedule(() => {
      if (played >= params.rounds) {
        setPhase("over");
        if (sessionIdRef.current) {
          savingRef.current = endSession(
            sessionIdRef.current,
            scoreGame(roundsCorrectRef.current, played),
            playClock.elapsedMs(),
          );
        }
        return;
      }
      const next = nextPathLength(pathLength, success, flowers.length);
      setRound(played);
      setPathLength(next);
      startRound(next);
    }, success ? SUCCESS_PAUSE_MS : MISTAKE_PAUSE_MS);
  };

  const handleFlowerClick = (flower) => {
    if (phase !== "play") return;
    // The player butterfly flies at the guide's speed. A quick follow-up tap
    // redirects it mid-flight, so only the latest flight's landing counts.
    flyTo(setPlayer, flower);
    clearTimeout(playerLandRef.current);
    playerLandRef.current = setTimeout(() => land(setPlayer), params.flightMs);
    timersRef.current.push(playerLandRef.current);

    const expectedId = path[progress];
    if (flower.id === expectedId) {
      bloom(flower.id);
      const done = progress + 1;
      setProgress(done);
      if (done === path.length) {
        setPhase("feedback");
        finishRound(true);
      }
    } else {
      setMistake({ wrongId: flower.id, expectedId });
      setPhase("feedback");
      finishRound(false);
    }
  };

  const flowerState = (id) => {
    if (mistake && id === mistake.wrongId) return "wrong";
    if (mistake && id === mistake.expectedId) return "hint";
    if (id === litId) return "lit";
    return null;
  };

  const flowerBadge = (id) => {
    if (mistake && id === mistake.wrongId) return { type: "wrong", text: "✗" };
    const pos = path.indexOf(id);
    if ((phase === "play" || phase === "feedback") && pos > -1 && pos < progress) {
      return { type: "done", text: String(pos + 1) };
    }
    return null;
  };

  const instruction = () => {
    switch (phase) {
      case "ready":
        return "Watch the orange butterfly, then tap the flowers it visited in the same order.";
      case "watch":
        return "Watch carefully…";
      case "play":
        return `Your turn! Tap the flowers in order (${progress} of ${path.length}).`;
      case "feedback":
        return mistake
          ? "Not quite — the dashed flower was next. Let's try another path."
          : "Perfect! 🦋";
      default:
        return "Getting the meadow ready…";
    }
  };

  if (phase === "over") {
    const played = params.rounds;
    return (
      <GameLayoutTopbar
        infoPanel={<h2 className="heading-secondary">Butterfly Path — Game Over</h2>}
        gameArea={
          <GameOverScreen
            stats={[
              `You remembered ${roundsCorrectRef.current} of ${played} paths.`,
              longestRef.current > 0
                ? `Longest path remembered: ${longestRef.current} flowers.`
                : "Keep practising — the paths get easier with time!",
            ]}
            onRestart={setupGame}
            showFireworks={roundsCorrectRef.current > 0}
            fireworksAnimation={fireworksData}
          />
        }
      />
    );
  }

  return (
    <GameLayoutTopbar
      className="butterfly-path"
      infoPanel={
        <>
          <h2 className="heading-secondary">Level {levelNumber}</h2>
          <h3 className="heading-tertiary">
            Butterfly Path
            {phase !== "loading" &&
              phase !== "ready" &&
              ` — Round ${round + 1} of ${params.rounds}`}
          </h3>
          <p className="text-muted bp-instruction" role="status">
            {instruction()}
          </p>
          <button
            type="button"
            className="bp-sound"
            onClick={() => setAudioOn((on) => !on)}
            aria-label={audioOn ? "Turn sound off" : "Turn sound on"}
          >
            {audioOn ? <FaVolumeUp /> : <FaVolumeMute />}
          </button>
        </>
      }
      gameArea={
        <div className="bp-board">
          {flowers.map((flower) => (
            <Flower
              key={flower.id}
              flower={flower}
              bloomKey={blooms[flower.id] || 0}
              state={flowerState(flower.id)}
              badge={flowerBadge(flower.id)}
              onClick={() => handleFlowerClick(flower)}
              disabled={phase !== "play"}
            />
          ))}
          <Butterfly
            className="bp-butterfly--guide"
            pos={guide}
            flying={guide.flying}
            flightMs={params.flightMs}
            animationData={guideButterflyData}
          />
          <Butterfly
            className={`bp-butterfly--player${phase === "play" || phase === "feedback" ? "" : " bp-butterfly--resting"}`}
            pos={player}
            flying={player.flying}
            flightMs={params.flightMs}
            animationData={playerButterflyData}
          />
          {phase === "ready" && (
            <div className="bp-overlay">
              <button
                type="button"
                className="btn btn-primary bp-start"
                onClick={startGame}
              >
                Start
              </button>
            </div>
          )}
        </div>
      }
    />
  );
};

export default ButterflyPathForm;
